/**
 * SaveManager — pure, framework-free persistence layer.
 *
 * No Phaser import. Works in Node tests via constructor-injected StorageAdapter.
 * Production code calls getSaveManager() which lazily binds to window.localStorage.
 *
 * Schema versioning: every SaveSlotData carries `schemaVersion`. When the schema
 * advances, add a migration branch in `migrate()` below — old data upgrades on
 * first load rather than being lost.
 */

import type { Difficulty } from '@/state/GameState';
import type { BloodLevel } from '@/systems/HitJuice';
import type { ProfanityLevel } from '@/systems/LanguageFilter';
import { computeGrantedTiers } from '@/systems/rewardTiers';
import { isSurvivalNewRecord, type SurvivalRecord } from '@/systems/SurvivalScoring';

export type { SurvivalRecord };

export type { BloodLevel };
export type { ProfanityLevel };

// ── Storage adapter (injectable for tests) ─────────────────────────────────

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// ── Schema ──────────────────────────────────────────────────────────────────

export const SAVE_SCHEMA_VERSION = 1;
export const SAVE_SLOT_COUNT = 3;
export const SAVE_KEY_PREFIX = 'manor.save';

export interface GameOptions {
  musicVolume: number;
  sfxVolume: number;
  voiceVolume: number;
  /**
   * Master output volume (MY-2028). Optional so old saves migrate cleanly.
   * Absent saves default to DEFAULT_MASTER_VOLUME (1.0) via migrate().
   */
  masterVolume?: number;
  /**
   * Master mute toggle (MY-2028). Optional so old saves migrate cleanly.
   */
  masterMuted?: boolean;
  bloodEnabled: boolean;
  screenShakeEnabled: boolean;
  /** BCP-47 locale tag. Only 'en' is supported in the first pass. */
  language: string;
  /**
   * Blood/gore fidelity: 'off' = none, 'normal' = blood particles,
   * 'gore' = blood + cel-gib burst on super/finisher KOs (MY-1967).
   * Optional so old save-data and existing tests compile without a migration bump.
   */
  bloodLevel?: BloodLevel;
  /**
   * Profanity/language level for dialogue, taunts, and VO barks (MY-2004).
   * 'off' = clean set; 'censored' = masked text + bleeped VO; 'full' = uncut.
   * Default 'full', gated behind first-run age confirmation.
   * Optional so old save-data migrates cleanly without a schema bump.
   */
  profanityLevel?: ProfanityLevel;
  /**
   * Enable SF-style motion inputs (QCF / DP / charge) as an alternative to
   * direction+SPECIAL for hero specials (MY-2419). Off by default — opt-in
   * for players who want fighting-game depth.
   * Optional so old save-data migrates cleanly without a schema bump.
   */
  motionInputEnabled?: boolean;
}

export const DEFAULT_OPTIONS: Readonly<GameOptions> = {
  masterVolume: 1.0,
  masterMuted: false,
  musicVolume: 0.8,
  sfxVolume: 1.0,
  voiceVolume: 1.0,
  bloodEnabled: true,
  screenShakeEnabled: true,
  language: 'en',
  bloodLevel: 'normal',
  profanityLevel: 'full',
  motionInputEnabled: false,
};

/** Heroes unlocked from the start (mirrors lockedByDefault in heroes.ts). */
export const DEFAULT_UNLOCKED_HEROES: readonly string[] = ['ray', 'errol', 'kris', 'priya'];

/** Difficulties available without progression. */
export const DEFAULT_UNLOCKED_DIFFICULTIES: readonly Difficulty[] = ['pint', 'pub'];

export interface SaveSlotData {
  schemaVersion: number;
  slotIndex: number;
  /** Hero used in this slot's last run. */
  heroId: string | null;
  /** 0 = nothing cleared yet; 1-9 = highest stage beaten. */
  highestStageCleared: number;
  unlockedDifficulties: Difficulty[];
  unlockedHeroIds: string[];
  options: GameOptions;
  /** Difficulty chosen in the last session — restored on CharSelectScene load. */
  lastDifficulty?: Difficulty;
  /** How many times the player has accepted Halloway's deal in the current run. */
  tookDeal: number;
  /** Ids of PM fate endings seen across all playthroughs (MY-1979). */
  endingsSeen: string[];
  /**
   * Reward tier thresholds that have already been granted/fired (MY-1991).
   * Values match REWARD_TIERS[].threshold (5, 10, 14).
   * Used to trigger one-time celebrations without re-firing on every load.
   */
  rewardTiersGranted?: number[];
  /** True once the secret True End cutscene has played (MY-1991). */
  trueEndSeen?: boolean;
  /** Whether MANOR Mode is currently toggled on for this slot (MY-1991). */
  manorModeEnabled?: boolean;
  /** Best Survival-mode run for this slot (highest score). Null until first run. */
  survivalRecord?: SurvivalRecord | null;
  /** Unix ms timestamp of last write (0 = never saved). */
  lastSaved: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function storageKey(slotIndex: number): string {
  return `${SAVE_KEY_PREFIX}.v${SAVE_SCHEMA_VERSION}.slot${slotIndex}`;
}

function defaultSlot(slotIndex: number): SaveSlotData {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    slotIndex,
    heroId: null,
    highestStageCleared: 0,
    unlockedDifficulties: [...DEFAULT_UNLOCKED_DIFFICULTIES],
    unlockedHeroIds: [...DEFAULT_UNLOCKED_HEROES],
    options: { ...DEFAULT_OPTIONS },
    tookDeal: 0,
    endingsSeen: [],
    rewardTiersGranted: [],
    trueEndSeen: false,
    manorModeEnabled: false,
    survivalRecord: null,
    lastSaved: 0,
  };
}

/**
 * Upgrade persisted data to the current schema.
 * Add `case N:` branches here as the schema evolves.
 */
function migrate(raw: Partial<SaveSlotData>, slotIndex: number): SaveSlotData {
  const base = defaultSlot(slotIndex);
  // Merge known fields over defaults; unknown future fields are dropped.
  return {
    ...base,
    ...raw,
    schemaVersion: SAVE_SCHEMA_VERSION,
    slotIndex,
    // Re-apply defaults for any sub-objects that may be missing keys.
    options: { ...DEFAULT_OPTIONS, ...(raw.options ?? {}) },
  };
}

// ── SaveManager class ────────────────────────────────────────────────────────

export type SavePatch = Partial<
  Pick<
    SaveSlotData,
    | 'heroId'
    | 'highestStageCleared'
    | 'unlockedDifficulties'
    | 'unlockedHeroIds'
    | 'options'
    | 'lastDifficulty'
    | 'tookDeal'
    | 'endingsSeen'
    | 'rewardTiersGranted'
    | 'trueEndSeen'
    | 'manorModeEnabled'
    | 'survivalRecord'
  >
>;

export class SaveManager {
  constructor(private readonly storage: StorageAdapter) {}

  /** Write a patch over the existing slot data (or a fresh default). */
  save(slotIndex: number, patch: SavePatch, timestamp: number): SaveSlotData {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    const updated: SaveSlotData = {
      ...existing,
      ...patch,
      schemaVersion: SAVE_SCHEMA_VERSION,
      slotIndex,
      lastSaved: timestamp,
    };
    this.storage.setItem(storageKey(slotIndex), JSON.stringify(updated));
    return updated;
  }

  /** Load slot data, migrating from an older schema if necessary. Returns null if slot is empty. */
  load(slotIndex: number): SaveSlotData | null {
    const raw = this.storage.getItem(storageKey(slotIndex));
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<SaveSlotData>;
      return migrate(parsed, slotIndex);
    } catch {
      return null;
    }
  }

  /** Erase a save slot. */
  clear(slotIndex: number): void {
    this.storage.removeItem(storageKey(slotIndex));
  }

  /** Returns an array of SAVE_SLOT_COUNT entries (null = empty slot). */
  getAllSlots(): (SaveSlotData | null)[] {
    return Array.from({ length: SAVE_SLOT_COUNT }, (_, i) => this.load(i));
  }

  /**
   * Auto-save on stage clear.
   * Updates highestStageCleared only if the new value exceeds the stored one.
   */
  autoSave(
    slotIndex: number,
    stageCleared: number,
    heroId: string | null,
    tookDeal: number,
    timestamp: number,
  ): SaveSlotData {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    const highestStageCleared = Math.max(existing.highestStageCleared, stageCleared);
    return this.save(slotIndex, { heroId, highestStageCleared, tookDeal }, timestamp);
  }

  /** Add a difficulty to the unlocked list if not already present. */
  /**
   * Persist a Survival run as this slot's record iff it beats the stored score.
   * Returns true when a new record was written.
   */
  recordSurvival(slotIndex: number, record: SurvivalRecord, timestamp: number): boolean {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    if (!isSurvivalNewRecord(record.score, existing.survivalRecord)) return false;
    this.save(slotIndex, { survivalRecord: record }, timestamp);
    return true;
  }

  unlockDifficulty(slotIndex: number, difficulty: Difficulty, timestamp: number): SaveSlotData {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    // Already unlocked (incl. a default on a never-saved slot): persist the record
    // so the slot is materialised, but add no duplicate entry.
    if (existing.unlockedDifficulties.includes(difficulty))
      return this.save(slotIndex, {}, timestamp);
    return this.save(
      slotIndex,
      { unlockedDifficulties: [...existing.unlockedDifficulties, difficulty] },
      timestamp,
    );
  }

  /** Add a hero to the unlocked list if not already present. */
  unlockHero(slotIndex: number, heroId: string, timestamp: number): SaveSlotData {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    // Already unlocked (incl. a default on a never-saved slot): persist the record
    // so the slot is materialised, but add no duplicate entry.
    if (existing.unlockedHeroIds.includes(heroId)) return this.save(slotIndex, {}, timestamp);
    return this.save(
      slotIndex,
      { unlockedHeroIds: [...existing.unlockedHeroIds, heroId] },
      timestamp,
    );
  }

  /** Persist updated options for a slot. */
  saveOptions(slotIndex: number, options: GameOptions, timestamp: number): SaveSlotData {
    return this.save(slotIndex, { options }, timestamp);
  }

  /**
   * Record an ending as seen if not already in the list.
   * Feeds the Endings Gallery (MY-1979 / separate gallery ticket).
   */
  recordEndingSeen(slotIndex: number, endingId: string, timestamp: number): SaveSlotData {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    if (existing.endingsSeen.includes(endingId)) return this.save(slotIndex, {}, timestamp);
    return this.save(slotIndex, { endingsSeen: [...existing.endingsSeen, endingId] }, timestamp);
  }

  /**
   * Evaluate which reward tiers should now be granted based on endingsSeen,
   * persist any newly earned tiers, and return the thresholds that are
   * newly granted this call (empty array = nothing new).
   *
   * Callers should react to a non-empty return value by triggering
   * tier-unlock cutscenes / celebrations (MY-1991).
   */
  grantRewardTiers(
    slotIndex: number,
    timestamp: number,
  ): { newlyGranted: number[]; data: SaveSlotData } {
    const existing = this.load(slotIndex) ?? defaultSlot(slotIndex);
    const alreadyGranted: number[] = existing.rewardTiersGranted ?? [];
    const earned = existing.endingsSeen.length;

    const allEarned = computeGrantedTiers(earned);
    const newlyGranted = allEarned.filter((t) => !alreadyGranted.includes(t));

    if (newlyGranted.length === 0) {
      return { newlyGranted: [], data: existing };
    }
    const updated = this.save(
      slotIndex,
      { rewardTiersGranted: [...alreadyGranted, ...newlyGranted] },
      timestamp,
    );
    return { newlyGranted, data: updated };
  }

  /** Mark the True End cutscene as having been seen (fires once, MY-1991). */
  markTrueEndSeen(slotIndex: number, timestamp: number): SaveSlotData {
    return this.save(slotIndex, { trueEndSeen: true }, timestamp);
  }

  /** Toggle MANOR Mode on or off for this slot (MY-1991). */
  setManorMode(slotIndex: number, enabled: boolean, timestamp: number): SaveSlotData {
    return this.save(slotIndex, { manorModeEnabled: enabled }, timestamp);
  }
}

// ── Production singleton (lazy — safe at module import time in Node) ─────────

let _instance: SaveManager | null = null;

/** Returns the shared production SaveManager backed by window.localStorage. */
export function getSaveManager(): SaveManager {
  if (!_instance) {
    _instance = new SaveManager(localStorage);
  }
  return _instance;
}

/** Active slot index used for auto-save during gameplay (slot selection UI is a future ticket). */
export const ACTIVE_SLOT = 0;
